import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { Keyboard, View } from 'react-native'
import { useNavigation, useRoute } from '@react-navigation/native'
import { get } from 'lodash'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { Controller, FieldValues, useForm } from 'react-hook-form'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text } from '@components/base'
import { NavigationContainer } from '@screens/Common/containers'
import { clientConfigStoreStateSelector, labResultsFileUploadLimitMBSelector } from '@selectors/app'
import { NavigatorContext, useGoBack, useSnack } from '@utils'
import { BiomarkerConfig } from '@src/types'
import { ScrollableAvoidKeyboard, SelectDateTime, useActionBottomSheet } from '@src/components'
import { DATE_FORMAT, MONTH_NAME_AND_DATE_WITH_YEAR_FORMAT } from '@src/config/momentFormat'
import { AppRouteProp, AppStackParamList } from '@src/navigation/types'
import { Analytics, CustomEventTypes, ErrorMessages } from '@src/config'
import {
  ImageRequestActionSheetOption,
  useImageRequestActionSheet,
} from '@src/hooks/useImageRequestActionSheet'
import { ReactNativeFile } from '@src/utils/image'
import { SelectDateTimeRef } from '@src/components/SelectDateTime'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { isInputValueValid } from '../utils'
import { LabUploadOption } from '../components/LabUploadOption'
import { BiomarkerFormInput } from '../components/BiomarkerFormInput'

const preparePayload = (formData: { [key: string]: any }) => {
  const healthMetricsData = Object.entries(formData)
    .filter(([key]) => key !== 'date')
    .map((entry) => ({
      kind: entry[0],
      value: Number.parseFloat(entry[1]),
    }))

  return {
    date: formData['date'],
    healthMetricsData,
  }
}

const closeIconDescriptor = { pack: 'eva', name: 'close' } as const

export const AddLabResults = () => {
  useSecureScreen()

  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const goBack = useGoBack()
  const clientConfig = useSelector(clientConfigStoreStateSelector)
  const showSnack = useSnack()
  const biomarkersConfig = get(clientConfig, 'biomarkers', [])
  const labResultsUploadLimitMB = useSelector(labResultsFileUploadLimitMBSelector)
  const dispatch = useDispatch()
  const showActionBottomSheet = useActionBottomSheet()
  const insets = useSafeAreaInsets()
  const route = useRoute<AppRouteProp<'AddLabResults'>>()
  const closeIcon = route.params?.closeIcon
  const selectDateTimeRef = useRef<SelectDateTimeRef>(null)
  const submitLoadingRef = useRef(false)
  const { enableLoader, disableLoader } = useContext(NavigatorContext)

  useEffect(
    () => () => {
      if (submitLoadingRef.current) {
        disableLoader()
        submitLoadingRef.current = false
      }
    },
    [disableLoader],
  )

  const { control, handleSubmit } = useForm({})

  const onValidationError = (errors: FieldValues) => {
    Keyboard.dismiss()
    if (errors.date) {
      selectDateTimeRef.current?.focus()
    }
  }

  const filterFormData = (formData: FieldValues) => {
    return Object.fromEntries(
      Object.entries(formData).filter(([, value]) => isInputValueValid(value)),
    )
  }

  const onValidationSuccess = (formData: FieldValues) => {
    Keyboard.dismiss()

    const data = filterFormData(formData)

    if (Object.keys(data).length === 1) {
      showActionBottomSheet({
        title: 'No data entered',
        body: 'Please enter at least one lab result to submit',
        primaryButton: {
          text: 'Dismiss',
        },
      })
      return
    }

    const payload = preparePayload(data)

    if (submitLoadingRef.current) {
      return
    }

    submitLoadingRef.current = true
    enableLoader()

    dispatch({
      payload,
      type: 'labResults/submit',
      failure: (error: any) => {
        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error')
        disableLoader()
        submitLoadingRef.current = false
      },
      success: () => {
        Analytics.track(CustomEventTypes.OutcomesTrackingLabResultsSubmitted)
        disableLoader()
        submitLoadingRef.current = false
        if (closeIcon) {
          // we have entered from the home screen, we need to see the lab results screen after adding
          return navigation.navigate('LabResultsScreen')
        }
        goBack()
      },
    })
  }

  const submitResults = handleSubmit(onValidationSuccess, onValidationError)

  const handleCancelPress = useCallback(() => {
    Analytics.track(CustomEventTypes.OutcomesTrackingNewLabResultsCancelTapped)
    goBack()
  }, [goBack])

  const onFileUpload = useCallback(
    (file: ReactNativeFile) => {
      navigation.navigate('UploadLabResults', { file })
    },
    [navigation],
  )
  const { showImageRequestActionSheet } = useImageRequestActionSheet(onFileUpload)

  const leftIcon = closeIcon ? closeIconDescriptor : undefined

  const uploadLabCard = useMemo(() => {
    const onUploadFilePress = () =>
      showImageRequestActionSheet({
        title: 'Choose Lab Result',
        actionSheetStyleOptions: { cancelButtonTintColor: 'red' },
        options: [
          ImageRequestActionSheetOption.ChooseFromLibrary,
          ImageRequestActionSheetOption.TakePhoto,
          ImageRequestActionSheetOption.ChooseFile,
        ],
      })

    return (
      <LabUploadOption
        onPress={() => {
          onUploadFilePress()
        }}
        label="Upload Lab Results"
        icon="cloud-arrow-up"
        fileSizeLimitMb={labResultsUploadLimitMB}
      />
    )
  }, [labResultsUploadLimitMB, showImageRequestActionSheet])

  return (
    <NavigationContainer leftIcon={leftIcon} title="Add Lab Results" goBack={handleCancelPress}>
      <ScrollableAvoidKeyboard
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 16 }]}
      >
        <Text type="regular" style={styles.descriptionText}>
          Track all of your key health metrics and biomarkers in one place.
        </Text>
        <View style={styles.uploadOptions}>{uploadLabCard}</View>
        <View style={styles.dividerContainer}>
          <View style={styles.divider} />
          <Text type="regular">or enter manually</Text>
          <View style={styles.divider} />
        </View>
        <Controller
          control={control}
          name="date"
          rules={{ required: 'Please select a date' }}
          render={({ field, fieldState }) => (
            <SelectDateTime
              inputProps={{
                label: 'Date of the lab test',
                accessoryLeft: <Icon name="calendar-blank" style={styles.icon} />,
                value: field.value
                  ? moment.utc(field.value).format(MONTH_NAME_AND_DATE_WITH_YEAR_FORMAT)
                  : undefined,
                hasError: fieldState.invalid,
                errorText: fieldState.error?.message,
              }}
              pickerProps={{
                mode: 'date',
                maximumDate: new Date(),
              }}
              ref={selectDateTimeRef}
              onChange={(value) => field.onChange(moment(value).format(DATE_FORMAT))}
              style={styles.datePicker}
            />
          )}
        />

        <Text type="large" bold>
          Biomarkers
        </Text>
        <View style={styles.form}>
          {[...biomarkersConfig]
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
            .map((biomarker: BiomarkerConfig) => (
              <BiomarkerFormInput
                key={biomarker.kind}
                control={control}
                label={biomarker.label}
                kind={biomarker.kind}
                unit={biomarker.unit || ''}
              />
            ))}
        </View>
      </ScrollableAvoidKeyboard>
      <View style={styles.buttons}>
        <Button
          accessibilityLabel="Cancel"
          type="outline"
          size="block"
          style={styles.cancelButton}
          onPress={handleCancelPress}
        >
          Cancel
        </Button>
        <View style={styles.buttonSpacer} />
        <Button
          accessibilityLabel="Save"
          type="primary"
          size="block"
          style={styles.saveButton}
          onPress={submitResults}
        >
          Save
        </Button>
      </View>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  content: {
    padding: 16,
  },
  icon: {
    width: 16,
    height: 16,
    color: 'theme.surface.base0',
  },
  form: {
    flex: 1,
  },
  descriptionText: {
    marginBottom: 24,
  },
  uploadOptions: {
    flex: 1,
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
    alignSelf: 'center',
    marginBottom: 16,
  },
  dividerContainer: {
    marginVertical: 16,
    alignItems: 'center',
    flexDirection: 'row',
  },
  divider: {
    height: 1,
    backgroundColor: 'theme.surface.base2',
    marginHorizontal: 8,
    flex: 1,
  },
  datePicker: {
    marginBottom: 24,
  },
  buttons: {
    flexDirection: 'row',
    marginBottom: 32,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  cancelButton: {
    flex: 1,
  },
  saveButton: {
    flex: 1.7,
  },
  buttonSpacer: {
    width: 8,
    height: 8,
  },
})
